import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Lightning, CurrencyGbp, Calendar, Plus, Check, Gauge } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    timingRecommendConsistent,
    timingRecommendExplosive,
    timingRecommendValue,
    timingRecommendValueNew,
    timingRecommendPeriod,
    timingRecommendFdrAbility,
} from "../../lib/timing-api";
import { timingPoolMatch } from "../../lib/timing-pool-api";
import { t, POSITIONS, PERIODS } from "../../lib/timing-i18n";
import { PositionPill } from "./TimingBits";

const FINDERS = [
    { key: "consistent", labelKey: "findersConsistent", icon: Trophy, fn: timingRecommendConsistent, scoreKey: "score", scoreExplain: "consistencyExplain" },
    { key: "explosive", labelKey: "findersExplosive", icon: Lightning, fn: timingRecommendExplosive, scoreKey: "score", scoreExplain: "explosivenessExplain", suffix: "%" },
    { key: "value", labelKey: "findersValue", icon: CurrencyGbp, fn: timingRecommendValue, scoreKey: "score", scoreExplain: "valueExplain" },
    { key: "valuenew", labelKey: "findersValueNew", icon: CurrencyGbp, fn: null, scoreKey: "score", scoreExplain: "valueNewExplain" },
    { key: "period", labelKey: "findersPeriod", icon: Calendar, fn: null, scoreKey: "ppg", scoreExplain: null },
    { key: "fdrability", labelKey: "findersFdrAbility", icon: Gauge, fn: null, scoreKey: "score", scoreExplain: "fdrAbilityExplain" },
];

const VS_BUCKETS = [
    ["hard", "Silní (FDR 4-5)"],
    ["medium", "Střední (FDR 3)"],
    ["easy", "Slabí (FDR 1-2)"],
];

export default function Finders({ lang, sharedPool }) {
    const [tab, setTab] = useState("consistent");
    const [position, setPosition] = useState("");
    const [topN, setTopN] = useState(20);
    const [minSeasons, setMinSeasons] = useState(2);
    const [period, setPeriod] = useState("gw1_6");
    const [sort, setSort] = useState("desc");
    const [periodMetric, setPeriodMetric] = useState("ppg");
    const [vsBucket, setVsBucket] = useState("hard");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const current = FINDERS.find((f) => f.key === tab);

    useEffect(() => {
        setLoading(true);
        const params = {
            top_n: topN,
            min_seasons: minSeasons,
            ...(position ? { position } : {}),
            only_active: true,
            sort,
        };
        const promise = tab === "period"
            ? timingRecommendPeriod(period, { ...params, metric: periodMetric })
            : tab === "fdrability"
                ? timingRecommendFdrAbility({ ...params, vs: vsBucket })
                : tab === "valuenew"
                    ? timingRecommendValueNew({ top_n: topN, sort, ...(position ? { position } : {}) })
                    : current.fn(params);
        promise
            .then((d) => setRows(d.results || []))
            .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
            .finally(() => setLoading(false));
    }, [tab, position, topN, minSeasons, period, sort, periodMetric, vsBucket, current]);

    const addToPool = async (playerKey, displayName) => {
        if (!sharedPool) return;
        try {
            const r = await timingPoolMatch([{ query: displayName }]);
            const m = r.matches?.[0];
            if (!m || !m.current) {
                toast.error("Aktuální FPL hráč nenalezen");
                return;
            }
            const inPool = sharedPool.pool.some((p) => p.fpl_id === m.current.fpl_id);
            if (inPool) {
                sharedPool.removePlayer(m.current.fpl_id);
                toast.info(`${displayName} odebrán z poolu`);
                return;
            }
            const ok = sharedPool.addPlayer({
                fpl_id: m.current.fpl_id,
                display_name: m.current.display_name,
                historical_slug: m.historical_slug || playerKey,
                position: m.current.position,
                team: m.current.team,
                current_price: m.current.price,
                ownership_pct: m.current.ownership_pct,
                seasons_available: m.seasons_available || 0,
            });
            if (ok) toast.success(`${m.current.display_name} přidán do poolu`);
            else toast.info("Pool je plný");
        } catch {
            toast.error("Přidání selhalo");
        }
    };

    return (
        <div className="space-y-4" data-testid="timing-finders">
            {/* Finder tabs */}
            <div className="border border-white/10 bg-[#0d0d0d] p-3">
                <div className="flex gap-1.5 flex-wrap">
                    {FINDERS.map((f) => {
                        const Icon = f.icon;
                        return (
                            <button
                                key={f.key}
                                type="button"
                                onClick={() => setTab(f.key)}
                                data-testid={`finder-tab-${f.key}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1.5 border ${tab === f.key ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                <Icon size={11} weight={tab === f.key ? "fill" : "regular"} />
                                {t(f.labelKey, lang)}
                            </button>
                        );
                    })}
                </div>
                {current.scoreExplain && (
                    <p className="font-mono text-[10px] italic text-white/45 mt-2">
                        {t(current.scoreExplain, lang)}
                    </p>
                )}
            </div>

            {/* Filters */}
            <div className="border border-white/10 bg-[#0d0d0d] p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                        {t("position", lang)}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPosition("")}
                        data-testid="finder-pos-all"
                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${!position ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {t("allPositions", lang)}
                    </button>
                    {POSITIONS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPosition(p)}
                            data-testid={`finder-pos-${p}`}
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${position === p ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                <label className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                    {t("topN", lang)}
                    <input
                        type="number"
                        min={5}
                        max={100}
                        value={topN}
                        onChange={(e) => setTopN(Math.max(5, Math.min(100, parseInt(e.target.value) || 20)))}
                        data-testid="finder-topn"
                        className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-14 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"
                    />
                </label>
                <label className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                    {t("minSeasons", lang)}
                    <input
                        type="number"
                        min={1}
                        max={5}
                        value={minSeasons}
                        onChange={(e) => setMinSeasons(Math.max(1, Math.min(5, parseInt(e.target.value) || 2)))}
                        data-testid="finder-min-seasons"
                        className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-12 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"
                    />
                </label>

                <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Sort</span>
                    {[["desc", "↓ Sestupně"], ["asc", "↑ Vzestupně"]].map(([k, l]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setSort(k)}
                            data-testid={`finder-sort-${k}`}
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${sort === k ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            {l}
                        </button>
                    ))}
                </div>

                {tab === "fdrability" && (
                    <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Proti</span>
                        {VS_BUCKETS.map(([k, l]) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setVsBucket(k)}
                                data-testid={`finder-vs-${k}`}
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${vsBucket === k ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                )}

                {tab === "period" && (
                    <>
                    <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                            {t("period", lang)}
                        </span>
                        {PERIODS.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => setPeriod(p.key)}
                                data-testid={`finder-period-${p.key}`}
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${period === p.key ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                {t(p.labelKey, lang)}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Metrika</span>
                        {[["ppg", t("metricPpg", lang)], ["deviation", t("metricDeviation", lang)]].map(([k, l]) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setPeriodMetric(k)}
                                data-testid={`finder-metric-${k}`}
                                title={k === "deviation" ? "Fázové PPG mínus dlouhodobý průměr hráče — kdo v této fázi hraje nad/pod své poměry" : "Čisté body na zápas v dané fázi"}
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${periodMetric === k ? "bg-fa-mint text-black border-fa-mint" : "border-white/15 text-white/60 hover:text-white"}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    </>
                )}
            </div>

            {/* Results table */}
            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="finder-results">
                <table className="w-full min-w-[640px]">
                    <thead className="bg-black/40">
                        <tr>
                            <th className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">#</th>
                            <th className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">Player</th>
                            <th className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">{t("position", lang)}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">
                                {tab === "period"
                                    ? (periodMetric === "deviation" ? t("metricDeviation", lang) : t("ppg", lang))
                                    : t(current.labelKey, lang)}
                            </th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">
                                {tab === "period" ? "GW / PTS" : t("totalPoints", lang)}
                            </th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">{t("seasons", lang)}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">Pool</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={7} className="px-3 py-6 text-center font-mono text-[10px] italic text-white/40">
                                    {t("loading", lang)}
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-6 text-center font-mono text-[10px] italic text-white/40">
                                    {t("noData", lang)}
                                </td>
                            </tr>
                        )}
                        {rows.map((r, i) => {
                            const inPool = sharedPool?.pool.some(
                                (p) => p.historical_slug === r.player_key
                            );
                            const rowKey = r.player_key || `fpl-${r.fpl_id}`;
                            return (
                            <tr key={rowKey} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`finder-row-${i}`}>
                                <td className="px-3 py-2 font-mono text-[10px] tabular-nums text-white/45">{i + 1}</td>
                                <td className="px-3 py-2">
                                    <Link to={`/players/${encodeURIComponent(r.player_key || r.fpl_id)}`}
                                          data-testid={`finder-profile-${i}`}
                                          className="font-display font-black text-sm hover:text-fa-mint transition-colors">{r.display_name}</Link>
                                </td>
                                <td className="px-3 py-2"><PositionPill positions={r.positions} /></td>
                                <td className="px-3 py-2 text-right font-mono text-[12px] tabular-nums font-bold">
                                    {tab === "period" ? (
                                        periodMetric === "deviation" ? (
                                            <div>
                                                <span className={r.deviation >= 0 ? "text-fa-mint" : "text-red-300"}>
                                                    {r.deviation >= 0 ? "+" : ""}{r.deviation?.toFixed(2)}
                                                </span>
                                                <div className="text-[9px] text-white/45 font-normal">
                                                    fáze {r.ppg?.toFixed(2)} · Ø {r.overall_ppg?.toFixed(2)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="text-fa-yellow">{r.ppg?.toFixed(2)}</span>
                                                {r.deviation != null && (
                                                    <div className={`text-[9px] font-normal ${r.deviation >= 0 ? "text-fa-mint/80" : "text-red-300/80"}`}>
                                                        {r.deviation >= 0 ? "+" : ""}{r.deviation.toFixed(2)} vs Ø
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    ) : tab === "fdrability" ? (
                                        <div>
                                            <span className="text-fa-yellow">{r.score?.toFixed(2)}</span>
                                            <div className={`text-[9px] font-normal ${r.delta >= 0 ? "text-fa-mint/80" : "text-red-300/80"}`}>
                                                {r.delta >= 0 ? "+" : ""}{r.delta?.toFixed(2)} vs Ø {r.overall_ppg?.toFixed(2)}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-fa-yellow">{r.score}{current.suffix ? current.suffix : ""}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-[10px] tabular-nums text-white/60">
                                    {tab === "period"
                                        ? `${r.played} / ${r.points}`
                                        : tab === "fdrability"
                                            ? `${r.games} zápasů`
                                            : tab === "valuenew"
                                                ? `£${r.new_price} · ${r.total_points} pts`
                                                : `${r.total_points} · ${r.games_played}GW`}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-[9px] uppercase tracking-widest text-white/50">
                                    {tab === "fdrability" ? `${r.seasons_count}×`
                                        : tab === "valuenew" ? `${Math.round((r.minutes || 0) / 90)} z.`
                                        : `${(r.seasons || []).length}×`}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {sharedPool && (
                                        <button
                                            type="button"
                                            onClick={() => addToPool(r.player_key, r.display_name)}
                                            data-testid={`finder-add-${i}`}
                                            className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-1 border ${inPool ? "text-fa-mint border-fa-mint/50 bg-fa-mint/5" : "text-fa-sky border-fa-sky/40 hover:bg-fa-sky hover:text-black"}`}
                                            title={inPool ? "V poolu — klikni pro odebrání" : "Přidat do poolu"}
                                        >
                                            {inPool ? <Check size={10} weight="bold" /> : <Plus size={10} weight="bold" />}
                                        </button>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

